import { toTagOptions } from '../../../../../utils/transformers';
import {
  getPartyDetailsGridKey,
  isAdditionalClientDetailsRequired,
  PARTY_DETAILS_ADD_MORE_KEY,
  PARTY_DETAILS_DEFAULT_MAX_ROWS,
  PARTY_DETAILS_GRID_KEY,
  PARTY_DETAILS_MAX_ROWS,
} from './constants';

export const GIVEN_OR_RECEIVED_BY_OPTIONS = [
  { label: 'Existing Client', value: 'existingClient', disabled: false },
  { label: 'Prospective Client', value: 'prospectiveClient', disabled: false },
  { label: 'Other Third Party', value: 'otherThirdParty', disabled: false },
  { label: 'Supplier', value: 'supplier', disabled: false },
] as const;

export type PartyKind = 'existingClient' | 'prospectiveClient' | 'thirdParty';

export const PARTY_KINDS: readonly PartyKind[] = [
  'existingClient',
  'prospectiveClient',
  'thirdParty',
] as const;

export const PARTY_DISPLAY_COLUMNS = {
  type: 'partyType',
  name: 'partyName',
  individuals: 'partyIndividuals',
  relationship: 'partyRelationship',
} as const;

const ROW_META_KEYS = [
  'id',
  'givenOrReceivedBy',
  'additionalClientDetailsRequired',
] as const;

export const PARTY_ROW_FIELDS: Record<PartyKind, readonly string[]> = {
  existingClient: ['existingClientNumber', 'existingClientContactName'],
  prospectiveClient: [
    'prospectiveClientName',
    'prospectiveClientContactName',
  ],
  thirdParty: [
    'thirdPartyName',
    'thirdPartyContact',
    'thirdPartyRelationship',
  ],
};

const PARTY_MODAL_TITLES: Record<PartyKind, string> = {
  existingClient: 'Add existing client',
  prospectiveClient: 'Add prospective client',
  thirdParty: 'Add third party/supplier',
};

const isPartyDetailRow = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const getPartyKind = (value: unknown): PartyKind | null => {
  if (value === 'existingClient' || value === 'prospectiveClient') {
    return value;
  }

  if (
    value === 'otherThirdParty' ||
    value === 'supplier' ||
    value === 'thirdParty'
  ) {
    return 'thirdParty';
  }

  return null;
};

export const getPartyKindFromGridKey = (fieldName: unknown): PartyKind | null => {
  if (typeof fieldName !== 'string') {
    return null;
  }

  if (fieldName === getPartyDetailsGridKey('existingClient')) {
    return 'existingClient';
  }
  if (fieldName === getPartyDetailsGridKey('prospectiveClient')) {
    return 'prospectiveClient';
  }
  if (fieldName === getPartyDetailsGridKey('thirdParty')) {
    return 'thirdParty';
  }

  return null;
};

export const isPartyDetailsGridField = (fieldName: unknown): boolean =>
  fieldName === PARTY_DETAILS_GRID_KEY ||
  getPartyKindFromGridKey(fieldName) !== null;

export const getGivenOrReceivedByLabel = (value: unknown): string => {
  if (value === 'thirdParty') {
    return 'Third party/supplier';
  }

  const match = GIVEN_OR_RECEIVED_BY_OPTIONS.find(
    (option) => option.value === value
  );

  return match?.label ?? '';
};

export const getPartyModalTitle = (givenOrReceivedBy: unknown): string => {
  const partyKind = getPartyKind(givenOrReceivedBy);
  if (!partyKind) {
    return 'Add Details';
  }

  return PARTY_MODAL_TITLES[partyKind];
};

export const getPartyDetailRows = (value: unknown): Record<string, unknown>[] => {
  if (Array.isArray(value)) {
    return value.filter(isPartyDetailRow);
  }

  if (isPartyDetailRow(value) && Array.isArray(value.data)) {
    return value.data.filter(isPartyDetailRow);
  }

  return [];
};

const formatDisplayValue = (value: unknown): string => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  const tags = toTagOptions(value);
  if (tags.length > 0) {
    return tags.map((tag) => tag.label).join(', ');
  }

  return '';
};

export const getPartyDisplayName = (row: Record<string, unknown>): string => {
  const partyKind = getPartyKind(row.givenOrReceivedBy);
  if (partyKind === 'existingClient') {
    return formatDisplayValue(row.existingClientNumber);
  }
  if (partyKind === 'prospectiveClient') {
    return formatDisplayValue(row.prospectiveClientName);
  }
  if (partyKind === 'thirdParty') {
    return formatDisplayValue(row.thirdPartyName);
  }
  return '';
};

export const getPartyDisplayIndividuals = (
  row: Record<string, unknown>
): string => {
  const partyKind = getPartyKind(row.givenOrReceivedBy);
  if (partyKind === 'existingClient') {
    return formatDisplayValue(row.existingClientContactName);
  }
  if (partyKind === 'prospectiveClient') {
    return formatDisplayValue(row.prospectiveClientContactName);
  }
  if (partyKind === 'thirdParty') {
    return formatDisplayValue(row.thirdPartyContact);
  }
  return '';
};

export const getPartyDisplayRelationship = (
  row: Record<string, unknown>
): string => {
  if (getPartyKind(row.givenOrReceivedBy) !== 'thirdParty') {
    return '';
  }

  return formatDisplayValue(row.thirdPartyRelationship);
};

/**
 * Keeps only the selected party type's fields on the row about to be saved.
 */
export const stampPartyDetailRow = (row: Record<string, unknown>): void => {
  const partyKind = getPartyKind(row.givenOrReceivedBy);
  if (!partyKind) {
    return;
  }

  const allowed = new Set<string>([
    ...ROW_META_KEYS,
    ...PARTY_ROW_FIELDS[partyKind],
  ]);

  Object.keys(row).forEach((key) => {
    if (!allowed.has(key)) {
      delete row[key];
    }
  });
};

const normalizePartyDetailRow = (
  row: Record<string, unknown>,
  fallbackType?: unknown
): Record<string, unknown> => {
  const copy = { ...row };
  if (!getPartyKind(copy.givenOrReceivedBy) && getPartyKind(fallbackType)) {
    copy.givenOrReceivedBy = fallbackType;
  }
  stampPartyDetailRow(copy);
  return copy;
};

export const readPartyDetailRows = (
  data: Record<string, unknown> | undefined
): Record<string, unknown>[] => {
  if (!data) {
    return [];
  }

  if (data[PARTY_DETAILS_GRID_KEY] !== undefined) {
    return getPartyDetailRows(data[PARTY_DETAILS_GRID_KEY]).map((row) =>
      normalizePartyDetailRow(row, data.givenOrReceivedBy)
    );
  }

  return PARTY_KINDS.flatMap((partyKind) =>
    getPartyDetailRows(data[getPartyDetailsGridKey(partyKind)]).map((row) =>
      normalizePartyDetailRow(
        {
          ...row,
          givenOrReceivedBy: row.givenOrReceivedBy ?? partyKind,
        },
        data.givenOrReceivedBy
      )
    )
  );
};

export const hasPartyDetailGrid = (
  data: Record<string, unknown> | undefined
): boolean => {
  if (!data) {
    return false;
  }

  if (data[PARTY_DETAILS_GRID_KEY] !== undefined) {
    return true;
  }

  return PARTY_KINDS.some(
    (partyKind) => data[getPartyDetailsGridKey(partyKind)] !== undefined
  );
};

export const applyPartyDetailRowsToFormData = (
  data: Record<string, unknown>,
  rows: unknown,
  fallbackType?: unknown
): void => {
  data[PARTY_DETAILS_GRID_KEY] = getPartyDetailRows(rows).map((row) =>
    normalizePartyDetailRow(row, fallbackType)
  );

  PARTY_KINDS.forEach((partyKind) => {
    delete data[getPartyDetailsGridKey(partyKind)];
  });
};

const hasAdditionalClientDetailsAnswer = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '') {
    return false;
  }

  if (Array.isArray(value) && value.length === 0) {
    return false;
  }

  return true;
};

/**
 * Latest answer wins for Add entry:
 * - Yes → partyDetailsAddMore true
 * - No → partyDetailsAddMore false (disables Add entry)
 *
 * Prefers form-level additionalClientDetailsRequired (latest modal answer),
 * then the stored partyDetailsAddMore flag, then the last saved row.
 */
export const resolvePartyDetailsAddMore = (
  formLevelAdditionalClientDetailsRequired?: unknown,
  rows: Record<string, unknown>[] = [],
  explicitAddMore?: unknown
): boolean => {
  if (
    hasAdditionalClientDetailsAnswer(formLevelAdditionalClientDetailsRequired)
  ) {
    return isAdditionalClientDetailsRequired(
      formLevelAdditionalClientDetailsRequired
    );
  }

  if (typeof explicitAddMore === 'boolean') {
    return explicitAddMore;
  }

  if (rows.length === 0) {
    return false;
  }

  return isAdditionalClientDetailsRequired(
    rows[rows.length - 1]?.additionalClientDetailsRequired
  );
};

export const getPartyTableMaxRows = (
  rows: Record<string, unknown>[],
  formLevelAdditionalClientDetailsRequired?: unknown,
  explicitAddMore?: unknown
): number => {
  const addMore = resolvePartyDetailsAddMore(
    formLevelAdditionalClientDetailsRequired,
    rows,
    explicitAddMore
  );

  if (addMore) {
    return PARTY_DETAILS_MAX_ROWS;
  }

  // No / unanswered: keep existing rows, but do not allow another add.
  // With zero rows, still allow the first entry.
  return Math.max(rows.length, PARTY_DETAILS_DEFAULT_MAX_ROWS);
};

/** Form fields to sync whenever the latest additional-details answer changes. */
export const buildPartyDetailsAddMoreFormUpdate = (
  additionalClientDetailsRequired: unknown
): Record<string, unknown> => ({
  additionalClientDetailsRequired,
  [PARTY_DETAILS_ADD_MORE_KEY]: isAdditionalClientDetailsRequired(
    additionalClientDetailsRequired
  ),
});

/** Loose modal field: callers pass mixed field objects, not a single field interface. */
type ModalSchemaField = Record<string, unknown>;

type ModalSchemaSection = {
  title?: { text?: string; type?: string };
  fields?: ModalSchemaField[];
};

type ModalTableFormSchema = {
  formSchema?: {
    sections?: ModalSchemaSection[];
  };
};

const unwrapFieldValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.length > 0 ? unwrapFieldValue(value[0]) : undefined;
  }

  if (isPartyDetailRow(value) && 'value' in value) {
    return unwrapFieldValue(value.value);
  }

  return value;
};

/**
 * Shows or hides modal fields and updates the modal section title from the
 * selected party type. Mutates the live table form schema so FormDataTable
 * can re-render without remounting.
 *
 * Always keep additionalClientDetailsRequired visible once a party type is
 * selected — do not hide it after Yes.
 */
export const applyPartyModalFormState = (
  tableFormSchema: ModalTableFormSchema | undefined,
  givenOrReceivedBy: unknown
): void => {
  const section = tableFormSchema?.formSchema?.sections?.[0];
  if (!section?.fields) {
    return;
  }

  const partyType = unwrapFieldValue(givenOrReceivedBy);
  const partyKind = getPartyKind(partyType);

  section.title = {
    ...(section.title ?? {}),
    text: getPartyModalTitle(partyType),
    type: section.title?.type ?? 'h2',
  };

  section.fields.forEach((field) => {
    if (typeof field.name !== 'string' || field.name.length === 0) {
      return;
    }

    if (field.name === 'givenOrReceivedBy') {
      field.hidden = false;
      return;
    }

    if (field.name === 'additionalClientDetailsRequired') {
      field.hidden = partyKind === null;
      field.required = partyKind !== null;
      return;
    }

    if (!partyKind) {
      field.hidden = true;
      return;
    }

    field.hidden = !PARTY_ROW_FIELDS[partyKind].includes(field.name);
  });
};

export type PartyRowValidationResult =
  | { success: true; data: Record<string, unknown> }
  | { success: false; errors: Record<string, string> };

const isBlankPartyValue = (value: unknown): boolean =>
  formatDisplayValue(value) === '';

/**
 * Validates a party modal row, stamps typed fields, and returns either the
 * cleaned row or field errors for FormDataTable to display.
 */
export const validateAndStampPartyDetailRow = (
  row: Record<string, unknown>
): PartyRowValidationResult => {
  const givenOrReceivedBy = unwrapFieldValue(row.givenOrReceivedBy);
  row.givenOrReceivedBy = givenOrReceivedBy;

  const partyKind = getPartyKind(givenOrReceivedBy);
  const errors: Record<string, string> = {};

  if (!partyKind) {
    errors.givenOrReceivedBy =
      'Who was this given or received by? is required';
    return { success: false, errors };
  }

  stampPartyDetailRow(row);

  if (partyKind === 'existingClient' && isBlankPartyValue(row.existingClientNumber)) {
    errors.existingClientNumber = 'Client name is required';
  }

  if (
    partyKind === 'prospectiveClient' &&
    isBlankPartyValue(row.prospectiveClientName)
  ) {
    errors.prospectiveClientName = 'Prospective client name is required';
  }

  if (partyKind === 'thirdParty' && isBlankPartyValue(row.thirdPartyName)) {
    errors.thirdPartyName = 'Name of third party/supplier is required';
  }

  if (Object.keys(errors).length > 0) {
    return { success: false, errors };
  }

  return { success: true, data: row };
};

/** @deprecated Prefer validateAndStampPartyDetailRow */
export const getPartyRowValidationErrors = (
  row: Record<string, unknown>
): Record<string, string> | null => {
  const result = validateAndStampPartyDetailRow({ ...row });
  return result.success ? null : result.errors;
};

export const formatPartyDetailsDisplayCell = (
  columnKey: string,
  row: Record<string, unknown> | undefined
): string => {
  if (!row) {
    return '';
  }

  if (columnKey === PARTY_DISPLAY_COLUMNS.type || columnKey === 'givenOrReceivedBy') {
    return getGivenOrReceivedByLabel(row.givenOrReceivedBy);
  }

  if (columnKey === PARTY_DISPLAY_COLUMNS.name) {
    return getPartyDisplayName(row);
  }

  if (columnKey === PARTY_DISPLAY_COLUMNS.individuals) {
    return getPartyDisplayIndividuals(row);
  }

  if (columnKey === PARTY_DISPLAY_COLUMNS.relationship) {
    return getPartyDisplayRelationship(row);
  }

  return '';
};
